import type { ChamberFigureState } from '../ChamberFigure.tsx'

/**
 * 장면 모델 — **「무엇을 어떤 형태로 그리나」를 데이터로 낸다.**
 *
 * 🔴 **순수 함수다.** three · React · DOM 을 모르므로 jsdom 에서 그대로 시험된다.
 *    그리는 쪽(`ChamberParts.tsx`)은 이 표를 읽어 도형을 놓기만 한다.
 * 🔴 **색만으로 가르지 않는다.** 2D(`ChamberFigure`)가 쓰는 축 넷을 3D 에서는 **형태**로 옮겼다 —
 *
 * | 2D 축 | 3D 로 옮긴 형태 |
 * | --- | --- |
 * | ⓐ 채움 농도 | `fill` — 우물 안 젤·부유액의 **높이**(빈 · 절반 · 꽉) |
 * | ⓑ 테(실선·점선) | **3D 에는 없다** — 몸체는 «언제나» 실물 아크릴이다(아래 `unassigned` 주석) |
 * | ⓒ 표지 도형(`● ◐ ○ ▲ — ·`) | `cells`(바닥 세포 점) · `controller`(공정 중 얹히는 틀) · `hatch`(빗금 판) · `bar`(가로 막대) |
 * | ⓓ 글자 | 그림 밖 — 부르는 쪽이 `.a-sr` 로 붙인다(그림은 `aria-hidden`) |
 *
 * 색(`tone`)은 그 위에 **얹을 뿐**이다. 흑백 토큰만 있는 앱에서도 위 네 축이 그대로 선다.
 * 🔴 상태 6종은 **형태만으로 서로 달라야 한다**(`chamberScene.test.ts` 가 지문으로 검사한다).
 */

/** 우물 채움 높이. 🔴 **네 단계뿐이다** — 중간값을 만들어 상태를 늘리지 마라 */
export type ChamberFill = 0 | 0.25 | 0.5 | 1

/**
 * 우물 안 액체의 **정체** — 실물 공정의 그것이다(논문 Figure 1c).
 * `pegda` 젤 전구체(주입 직후 얕게) · `suspension` 세포 부유액 · `reagent` 염색 시약 ·
 * `gel` 굳은 젤(단계를 모를 때의 중립 채움).
 * 🔴 **색은 정체를 따라간다**(`chamberPalette.ts` `PROCESS`) — 상태 색이 아니다.
 */
export type ChamberLiquid = 'none' | 'pegda' | 'suspension' | 'reagent' | 'gel'

/**
 * 세포가 어떤 모습인가 — 🔴 **실물 공정 그림이 정한다.**
 * `floating` 원심 전(부유액 속에 떠 있다) · `settled` 원심 뒤(바닥에 한 층) ·
 * `stained` ICC 뒤(붉은 테로 표지).
 */
export type ChamberCells = 'none' | 'floating' | 'settled' | 'stained'

/** 공정 단계 — 🔴 **계약 `SampleStage` 어휘 그대로**다. 새 이름을 만들지 않는다 */
export type ChamberStage = 'registered' | 'centrifuged' | 'assembled' | 'staining' | 'completed'

/**
 * **그 챔버가 서 있는 공정 자리** — eric 2026-09-23 「원심분리기가 끝나면 하이픽 챔버한테
 * 예상되는 변화 모양이 있잖아. 그것을 표현 가능하게 해줘.」
 *
 * 🔴 **화면이 추론하지 않는다.** 배치 표지(`batchFlowCells`)가 말한 것을 그대로 받는다 —
 *    `done: false` 는 「그 단계를 지금 하고 있다」, `true` 는 「그 표지가 찍혔다」다.
 * 🔴 **안 넘기면 단계 모양이 없다**(상태 6종의 형태만). 2D 폴백이 그 자리다.
 */
export interface ChamberPhase {
  stage: ChamberStage
  done: boolean
}

/**
 * **단계마다 얹는 «도구 또는 효과 하나»** — eric 2026-09-28 「각각의 단계의 특징이 안 보이는데.. 그게 드러나도록 …
 * 각각의 단계 모두 심플하게 표현해줘」.
 *
 * | 도구 | 뜻 | 모양 |
 * | --- | --- | --- |
 * | `spin` | 원심분리기가 돈다 | 우물 위의 호 하나(화살 끝)가 돈다 · 세포가 젤 층으로 가라앉는다 |
 * | `press` | 어셈블러가 누른다 | 수직 팁(넓은 누름판)이 **위에서** 두께 조절기 틀을 눌렀다 올라간다 |
 * | `uv` | 어셈블러 UV 노광 | 램프 판 + **위로 향한 화살 셋** — 빛이 **아래에서** 든다 |
 * | `pipette` | 염색기가 분주한다 | 스포이드(고무 머리 + 가는 몸)가 위에서 방울을 떨군다 |
 *
 * 🔴 **한 챔버에 하나뿐이다.** 도구가 둘 겹치면 「지금 무엇을 하나」가 흐려진다.
 * 🔴 **색만으로 말하지 않는다** — 넷은 모양이 서로 다르다(호 · 끝이 넓은 누름 팁 · 위 화살 · 끝이 가는 스포이드).
 */
export type ChamberTool = 'none' | 'spin' | 'press' | 'uv' | 'pipette'

/**
 * **모듈이 지금 말하는 세부 국면** — 서버 모듈 상태(`ModuleSnapshot.state`)에서 온다(`screens/monitoring/chamberMotion.ts`).
 * `spin` 원심분리기가 `RUNNING` 이다 · `uv` 어셈블러가 `UV` 상태다 · `rest` 모듈이 그 단계에 있지만 주 동작을 안 한다
 * (원심 적재 · 회수 · 대기 · 염색기 `SHAKING`·`INCUBATING`·`WAITING`). **모르면 `undefined`** — 어셈블러 · 염색기는 그 단계의
 * 기본 도구가 서고, 🔴 **원심분리기는 아무것도 안 선다**(아래 `chamberTool`).
 */
export type ChamberModuleStep = 'spin' | 'uv' | 'rest'

/**
 * 공정 자리 «밖»에서 오는 두 가지 사실 — 🔴 **둘 다 서버가 말한 것이다.** 화면이 짐작하지 않는다.
 */
export interface ChamberDetail {
  /** 지금 도는 모듈의 세부 국면. `stage` 가 그 챔버의 진행 중 단계와 같을 때만 쓴다 */
  step?: { stage: ChamberStage; step: ChamberModuleStep } | undefined
  /**
   * 그 웰의 분주 기록(`chamberProgress.ts` · `basis: 'well'` 일 때만). `latest` 는 이번 한 바퀴에서
   * **가장 최근에 분주한 웰**이다 — 스포이드는 그 웰에만 선다(「지금 분주 중인 웰」은 서버가 안 말한다).
   */
  well?: { status: 'pending' | 'active' | 'done' | 'failed'; latest?: boolean } | undefined
  /**
   * 🔴 **염색기 피펫이 지금 이 웰에 들어가 있다**(`StainPipetteWellContext` · eric 2026-10-12). 스포이드는 이 웰에만 선다 —
   *    종전의 「모듈 근거면 아홉 칸 전부」 · 「가장 최근 분주 웰」 규칙은 걷었다(아홉 칸이 같이 스포이드를 얹어 무엇을 하는지 안 읽혔다)
   */
  pipetteHere?: boolean | undefined
}

/** 얹는 색 — 강조 1 · 위험 1 뿐이다(`--color-accent` · `--color-danger`) */
export type ChamberTone = 'plain' | 'accent' | 'danger'

export interface ChamberSceneModel {
  state: ChamberFigureState
  /** 젤 안정기를 꽂나 — 「이 챔버는 쓸 준비가 되어 있다」 */
  stabilizer: boolean
  /** 두께 조절기를 얹나 — 실물 공정에서 **UV 노광 직전에만** 얹힌다 = 「지금 하는 중」 */
  controller: boolean
  /** 우물 채움 높이 */
  fill: ChamberFill
  /** 채운 것이 무엇인가. `fill: 0` 이면 `none` */
  liquid: ChamberLiquid
  /**
   * 바닥의 **젤 층**(PEGDA · 등록 때 깔린다). 🔴 원심분리기가 세포를 **이 층 위로** 가라앉힌다 —
   * 「층이 갈린다」가 이 칸과 `cells` 의 높이로 보인다. 채움(`fill`)은 이 층 «위»의 액체다.
   */
  bed: boolean
  /** 세포의 모습 */
  cells: ChamberCells
  /**
   * **젤 층 위에 가라앉은 세포층** — 🔴 원심 완료 표지가 찍힌 자리에서만 선다(실패 · 폐기로 그 자리에 언 것 포함).
   * 2D 는 세포 원을 안 그려서(eric 2026-09-30) `cells: floating → settled` 가 그림에 안 보였다 — 원심 진행과 완료가
   * 윤곽 굵기로만 갈렸다. 그 차이를 **원 없이** 말하는 칸이다(바닥 안쪽 면 · `chamberOutline` `sediment`).
   * ⚠ 어셈블러 · 염색기 자리에는 세우지 않는다 — 거기서는 조절기 · 젤 막이 그 층을 덮는다(그 단계 그림은 이번 범위 밖).
   */
  sediment: boolean
  /**
   * 바닥을 덮은 **하이드로젤 막**(100 µm). 🔴 두께는 우리 눈금에서 안 보여 **면**으로 그린다
   * (`chamberGeometry.ts` 머리말) — UV 노광이 끝난 뒤(= 어셈블 완료)부터 있다.
   */
  pellicle: boolean
  /** UV 노광 중인가 — 아래에서 빛이 든다(실물 공정의 그 칸). 🔴 `tool: 'uv'` 와 늘 같이 선다 */
  uv: boolean
  /** 단계마다 얹는 도구 하나(`ChamberTool`) */
  tool: ChamberTool
  /** 우물을 덮는 **빗금 판** — 🔴 실패 전용(2D 해칭과 같은 뜻) */
  hatch: boolean
  /** 우물을 가로지르는 **막대 판** — 중단·폐기(2D `—` 와 같은 뜻) */
  bar: boolean
  /** 얹는 색 */
  tone: ChamberTone
  /** 숨쉬기(맥동). ⚠ `prefers-reduced-motion` 에서는 그리는 쪽이 0 으로 떨어뜨린다 */
  pulse: boolean
  /** 🔴 `flowVocabulary.ts` 글리프 표와 **같은 뜻**. 폴백 2D · 시험이 같이 읽는 이름표다 */
  glyph: string
}

/**
 * 상태 → 장면. 🔴 **여기 말고 어디서도 상태를 형태로 바꾸지 않는다.**
 *
 * 실물 공정 순서(논문 Figure 1c)를 그대로 빌려 쓴다 —
 * PEGDA 주입 → 안정기 삽입 → 세포 부유액 주입 → 원심분리 → 두께 조절기 삽입 → UV → 제거 → ICC.
 * 「아직 아무것도 안 넣음 = 빈 우물」, 「하는 중 = 조절기가 얹힌 절반」, 「지남 = 굳은 젤이 꽉」이
 * 실물의 모습과 같은 말이 되도록 골랐다.
 */
export function chamberScene(
  state: ChamberFigureState,
  phase?: ChamberPhase,
  detail?: ChamberDetail,
): ChamberSceneModel {
  const model = stateScene(state)
  /*
   * 🔴 **단계 모양은 상태 모양 «위에» 얹힌다**(eric 2026-09-23). 빗금(실패) · 막대(중단) ·
   *    강조 테 · 숨쉬기는 그대로 남고, 우물 «안»이 공정대로 바뀐다.
   * 🔴 **배정을 모르는 챔버에는 안 얹는다** — 누구 것인지 모르는데 공정을 그리면
   *    화면이 모르는 것을 아는 척한다. 챔버 «몸체»는 그려도 우물 «안»은 비어 있어야 한다.
   */
  if (phase === undefined || state === 'unassigned') return model
  const tool = chamberTool(state, phase, detail)
  /*
   * 🔴 두께 조절기는 **어셈블러 진행에만** 선다 — 상태 `active`(◐)가 조절기를 얹는 것은 단계를 모를 때의 말이다.
   *    단계를 알면 그 단계의 모습이 이긴다(원심 · 염색 진행 챔버에 조절기가 얹혀 도구를 가리던 일이 있었다).
   */
  return {
    ...model,
    controller: false,
    ...stagePatch(phase, detail, tool),
    tool,
    uv: tool === 'uv',
  }
}

/**
 * **단계 → 도구** — 🔴 **여기 한 곳이다**(시험이 표로 지킨다 · `chamberScene.test.ts`).
 *
 * | 단계 · 자리 | 세부(서버) | 도구 |
 * | --- | --- | --- |
 * | 등록(준비 중) | — | 없음 |
 * | 원심 진행 | `spin`(서버 `RUNNING`) | `spin` |
 * | 원심 진행 | `rest`(적재 · 회수 · 트레이 대기) · **모름** | 없음 |
 * | 어셈블러 진행 | 모름 · `TC_LOAD` 등 | `press` |
 * | 어셈블러 진행 | `uv` | `uv` |
 * | 염색 진행 | 피펫이 지금 이 웰에(`pipetteHere` · 분주 · 흡입 · 혼합) | `pipette` |
 * | 염색 진행 | 그 밖(다른 웰 · 시약 랙 · 폐기통 · 이동 · 흔들기 · 모름) | 없음 |
 * | 완료(어느 단계든) · 실패 · 중단 · 배정 모름 | — | 없음 |
 *
 * 🔴 **실패 · 중단은 도구를 안 얹는다** — 빗금 · 막대가 그 챔버의 말이다. 도구가 돌면 「아직 한다」로 읽힌다.
 * 🔴 **원심 회전 호는 서버가 `RUNNING` 이라고 말할 때만 선다**(2026-10-02 쵸단 · 시연 원심 검토). 종전에는 «모름»이 기본
 *    도구(회전)로 떨어져, 모듈 스냅샷이 낡았거나(`stale`) 배치 실행 모듈을 못 찾을 때 **멎은 원심을 도는 것으로** 그렸다.
 *    원심은 한 공정 안에서 적재 · 회전 · 회수를 회차마다 되풀이해서 「진행 = 돈다」가 아니다 — 모르면 안 돈다.
 */
export function chamberTool(
  state: ChamberFigureState,
  phase: ChamberPhase | undefined,
  detail?: ChamberDetail,
): ChamberTool {
  if (phase === undefined) return 'none'
  if (state === 'fail' || state === 'halted' || state === 'unassigned') return 'none'
  /*
   * 🔴 **피펫이 지금 이 웰에 있으면 그 웰의 «분주 완료»여도 선다**(dev2 2026-10-12 실측) — 피펫이 찾아가는 웰은 방금 분주를 받은(이번 바퀴
   *    완료) 웰이라 `done` 에 걸려 스포이드가 한 번도 안 섰다. 서버가 「피펫이 여기 있다」를 말하는 동안은 그 사실이 앞선다(흔들기 · 배양 제외)
   */
  if (phase.stage === 'staining' && detail?.pipetteHere === true && detail.step?.step !== 'rest')
    return 'pipette'
  if (phase.done) return 'none'
  const step = detail?.step?.stage === phase.stage ? detail.step.step : undefined
  switch (phase.stage) {
    case 'centrifuged':
      return step === 'spin' ? 'spin' : 'none'
    case 'assembled':
      return step === 'uv' ? 'uv' : 'press'
    case 'staining':
      /* 🔴 피펫이 지금 이 웰에 있을 때만(eric 2026-10-12) — 모르면 안 그린다 */
      return step !== 'rest' && detail?.pipetteHere === true ? 'pipette' : 'none'
    default:
      return 'none'
  }
}

/**
 * **단계 → 우물 안 모습** — 근거는 논문 Figure 1c 의 공정 8칸이고, 우리 모듈 3대에 어떻게
 * 대응시킬지는 **팀 판단**이다(`docs/figures/hypicc/README.md` §단계별 모양).
 *
 * | 단계 · 진행/완료 | 우물 안 |
 * | --- | --- |
 * | 등록 | 바닥에 젤 층(PEGDA)만 — 빈 챔버 |
 * | 원심 진행 | 젤 층 위 부유액 · 세포가 **떠 있다**(돌면 젤 층 쪽으로 가라앉는다) |
 * | 원심 완료 | 젤 층 위 부유액 · 세포가 **젤 층에 앉았다** — 🔴 `sediment`(세포층) 가 선다 |
 * | 어셈블 진행 | 두께 조절기가 얹힌다(팁이 누른다 · UV 면 아래에서 빛) |
 * | 어셈블 완료 | 조절기 제거 · 부유액을 따라냈다 · **젤 막**이 세포를 덮었다 |
 * | 염색 진행 | 시약이 절반(웰 근거면: 받은 웰은 꽉 · 준비 중 웰은 빈) |
 * | 염색 완료 · 완료 | **시약이 찬** 우물 · 세포가 **붉은 테로 표지**(ICC) |
 */
function stagePatch(
  phase: ChamberPhase,
  detail: ChamberDetail | undefined,
  tool: ChamberTool,
): Partial<ChamberSceneModel> {
  const { stage, done } = phase
  switch (stage) {
    case 'centrifuged':
      return {
        bed: true,
        fill: 1,
        liquid: 'suspension',
        cells: done ? 'settled' : 'floating',
        sediment: done,
      }
    case 'assembled':
      return done
        ? { bed: true, fill: 0, liquid: 'none', cells: 'settled', pellicle: true }
        : { bed: true, fill: 1, liquid: 'suspension', cells: 'settled', controller: true }
    case 'staining': {
      if (done) return STAINED
      const well = detail?.well
      /* 🔴 웰 근거 — 그 웰이 받았나(서버 분주 기록)가 채움을 정한다. 스포이드 자리는 방울이 떨어지는 중이라 절반 */
      const fill: ChamberFill =
        well === undefined || tool === 'pipette' ? 0.5 : well.status === 'pending' ? 0 : 1
      return {
        bed: true,
        fill,
        liquid: fill === 0 ? 'none' : 'reagent',
        cells: 'settled',
        pellicle: true,
      }
    }
    case 'completed':
      return STAINED
    /* 등록 — 젤 층만 깔렸다(공정 첫 칸). 🔴 「준비 중」은 빈 챔버다 */
    default:
      return { bed: true, fill: 0, liquid: 'none', cells: 'none' }
  }
}

/** 염색이 끝난 우물 — 시약이 차 있고 세포가 표지됐다(eric 2026-09-28 「염색기 완료 = 용액이 찬 상태」) */
const STAINED: Partial<ChamberSceneModel> = {
  bed: true,
  fill: 1,
  liquid: 'reagent',
  cells: 'stained',
  pellicle: true,
}

function stateScene(state: ChamberFigureState): ChamberSceneModel {
  switch (state) {
    /* ● 담김·지남 — 젤이 굳어 우물이 꽉 차고 바닥에 세포가 앉아 있다 */
    case 'marked':
      return base(state, {
        fill: 1,
        liquid: 'gel',
        cells: 'settled',
        glyph: '●',
      })
    /* ◐ 진행 중 — 부유액이 절반, 두께 조절기가 얹혀 있다(공정이 도는 모습) */
    case 'active':
      return base(state, {
        controller: true,
        fill: 0.5,
        liquid: 'gel',
        cells: 'settled',
        tone: 'accent',
        pulse: true,
        glyph: '◐',
      })
    /* ▲ 실패 — 꽉 찬 우물 위에 빗금 판이 덮인다. 🔴 빗금은 실패 전용이다 */
    case 'fail':
      return base(state, {
        fill: 1,
        liquid: 'gel',
        cells: 'settled',
        hatch: true,
        tone: 'danger',
        glyph: '▲',
      })
    /* — 중단·폐기 — 우물을 가로지르는 막대. 채움은 없고 세포도 없다 */
    case 'halted':
      return base(state, {
        bar: true,
        glyph: '—',
      })
    /*
     * · 배정 모름 — 🔴 **조립된 실물 챔버 그대로다**(eric 2026-09-23 「하이픽 챔버가 작업이
     * 진행 중이지 않을 때 투명색인데, **디폴트로 하이픽 챔버 3D 가 보이도록** 해줘」).
     * 종전에는 「부품 없는 반투명 윤곽」이었고, 그래서 **도는 작업이 없는 기본 화면에서
     * 챔버가 안 보였다** — 자리 카드만 서는 때가 바로 이 상태이기 때문이다.
     *
     * 🔴 **형태가 「대기」와 같아진다. 그래도 된다.**
     *    ⓐ 챔버 «하드웨어»는 실제로 같다. 갈리는 것은 「이 챔버가 누구 것인가」이지
     *       그릇의 생김새가 아니다 — 모르는 것을 도형으로 지어내면 그림이 거짓말을 한다.
     *    ⓑ 구별은 **글자**가 이미 맡는다 — 챔버 아래 한 마디가 **「미배정」·「배정 모름」**을
     *       적고(2026-09-26 카드 머리에서 내려왔다), 자리 카드는 웰 이름까지 빈 값이다. 색 채널이 아니라 글자
     *       채널이라 「색만으로 가르지 않는다」 규약을 어기지 않는다
     *       (시험: `sampleFlow.test.tsx` 「임시 목을 끄면 묶음 카드에 시료 ID 가 한 글자도 없다」).
     *    ⓒ 모델도 둘을 여전히 갈라 낸다 — `glyph` 이 `·` ↔ `○` 로 다르다.
     *       같아지는 것은 **3D 기하**(`chamberShapeKey`)뿐이다.
     * 🔴 **2D 폴백(`ChamberFigure`)의 점선 실루엣은 그대로다** — 이 지시의 대상이 아니다.
     */
    case 'unassigned':
      return base(state, { glyph: '·' })
    /* ○ 대기 — 안정기만 꽂힌 빈 우물 */
    default:
      return base(state, { glyph: '○' })
  }
}

/** 기본값 한 벌 — 갈리는 칸만 위에서 덮어쓴다(빠뜨린 칸이 생기지 않게) */
function base(
  state: ChamberFigureState,
  over: Partial<Omit<ChamberSceneModel, 'state'>>,
): ChamberSceneModel {
  return {
    state,
    stabilizer: true,
    controller: false,
    fill: 0,
    liquid: 'none',
    bed: false,
    cells: 'none',
    sediment: false,
    pellicle: false,
    uv: false,
    tool: 'none',
    hatch: false,
    bar: false,
    tone: 'plain',
    pulse: false,
    glyph: '○',
    ...over,
  }
}

/**
 * **형태 지문** — 색(`tone`)을 뺀 나머지. 🔴 이것이 상태마다 달라야
 * 「색을 못 보는 사람·흑백 앱」에서도 갈린다. 시험이 이 값으로 검사한다.
 *
 * ⚠ **「대기」와 「배정 모름」은 일부러 같은 값**이다(위 `unassigned` 주석 ⓑ) —
 *   그 둘의 구별은 기하가 아니라 **글자**가 맡는다. 나머지 다섯은 서로 달라야 한다.
 */
export function chamberShapeKey(model: ChamberSceneModel): string {
  return [
    model.stabilizer ? 'stab' : '-',
    model.controller ? 'ctrl' : '-',
    `fill${String(model.fill)}`,
    model.liquid,
    model.bed ? 'bed' : '-',
    model.cells,
    model.sediment ? 'sed' : '-',
    model.pellicle ? 'film' : '-',
    model.uv ? 'uv' : '-',
    model.tool,
    model.hatch ? 'hatch' : '-',
    model.bar ? 'bar' : '-',
  ].join('|')
}
